import styled from '@emotion/styled'
import { CardText } from '../../../cards/CardText/CardText'
import { CardMedia } from '../../../cards/CardMedia/CardMedia'
import { CardType, CollectionType } from './ContentCard'
import { WebLink, WebLinkProps, createPropFilter } from '@news-mono/web-common'
import { tokens } from '@news-mono/design-tokens'
import { breakpoint, calcRem, colors } from '../../../__styling'
import { keyframes } from '@emotion/react'

export interface StyledContentCardProps {
    cardType: CardType
    displayRightSeparator?: boolean
    collectionType?: CollectionType
}

export const StyledContentCard = styled('div')<StyledContentCardProps>(
    ({ cardType, displayRightSeparator }) => [
        cardType === 'small'
            ? {
                  display: 'grid',
                  gridTemplateColumns: '3fr 2fr',
                  columnGap: calcRem(13),
                  [breakpoint('lg')]: {
                      columnGap: calcRem(15),
                  },
              }
            : {},

        cardType === 'medium'
            ? {
                  display: 'grid',
                  [`${StyledCardImageContainer}`]: {
                      gridRow: 1,
                  },
              }
            : {},

        cardType === 'medium-video-hub'
            ? {
                  display: 'grid',
                  gridTemplateColumns: '3fr 2fr',
                  columnGap: calcRem(18),
                  [`${StyledCardImageContainer}`]: {
                      gridRow: 'unset',
                  },
                  [breakpoint('sm')]: {
                      gridTemplateColumns: 'unset',
                      [`${StyledCardImageContainer}`]: {
                          gridRow: 1,
                      },
                  },
              }
            : {},

        cardType === 'large'
            ? {
                  display: 'grid',
                  [`${StyledCardImageContainer}`]: {
                      gridRow: 1,
                  },

                  [breakpoint('sm')]: {
                      gridTemplateColumns: '3fr 2fr',
                      columnGap: calcRem(18),
                      [`${StyledCardImageContainer}`]: {
                          gridRow: 'unset',
                      },
                  },
                  [breakpoint('md')]: {
                      columnGap: calcRem(23),
                  },
              }
            : {},
        displayRightSeparator && {
            '&::after': {
                position: 'absolute',
                content: `''`,
                width: calcRem(1),
                height: calcRem(40),
                background: colors.sevennews.nero15,
                right: calcRem(-27),
                transform: 'translateY(50%)',
            },
        },
    ],
    {
        transition: 'opacity 0.3s ease',
        '&:hover': {
            opacity: '0.7',
        },
    },
)

export const StyledContentCardContainer = styled('div')<StyledContentCardProps>(
    ({ cardType, collectionType }) => [
        cardType === 'small' || cardType === 'large'
            ? {
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  [`@media (min-width: 768px)`]: {
                      justifyContent: 'space-between',
                  },
              }
            : {},
        cardType === 'small'
            ? {
                  '& [class*="StyledHeadline"]': {
                      fontWeight: 500,
                      fontSize: calcRem(14),
                      lineHeight: 'normal',

                      display: '-webkit-box',
                      WebkitBoxOrient: 'vertical',
                      WebkitLineClamp: 4,
                      overflow: 'hidden',

                      [breakpoint('md')]: {
                          fontSize: calcRem(12),
                      },
                      [breakpoint('lg')]: {
                          fontSize: calcRem(15),
                      },
                  },
              }
            : {},
        cardType === 'medium'
            ? {
                  '& [class*="StyledHeadline"],[class*="StyledTeaser"]': {
                      display: '-webkit-box',
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                  },
                  '& [class*="StyledHeadline"]': {
                      fontSize: calcRem(22),
                      lineHeight: calcRem(29),
                      marginBottom: 0,
                      WebkitLineClamp: 3,
                      fontWeight: 500,

                      [breakpoint('sm')]: {
                          fontSize: calcRem(18),
                          lineHeight: 'normal',
                          minHeight: calcRem(66),
                      },
                      [breakpoint('lg')]: {
                          fontSize: calcRem(21),
                          lineHeight: calcRem(27),
                          minHeight: calcRem(81),
                      },
                      [breakpoint('xl')]: {
                          fontSize: calcRem(22),
                          lineHeight: calcRem(29),
                          minHeight: calcRem(87),
                      },
                  },
                  '& [class*="StyledTeaser"]': {
                      fontSize: calcRem(17),
                      lineHeight: calcRem(22),
                      marginTop: calcRem(8),
                      WebkitLineClamp: 2,
                      fontWeight: 400,

                      [breakpoint('sm')]: {
                          fontSize: calcRem(13),
                          lineHeight: calcRem(19),
                          minHeight: calcRem(38),
                      },
                      [breakpoint('lg')]: {
                          fontSize: calcRem(15),
                          lineHeight: calcRem(22),
                      },
                      [breakpoint('xl')]: {
                          fontSize: calcRem(17),
                          minHeight: calcRem(44),
                      },
                  },
              }
            : {},
        cardType === 'medium-video-hub'
            ? {
                  '& [class*="StyledHeadline"]': {
                      fontSize: calcRem(14),
                      lineHeight: 'normal',
                      marginBottom: 0,
                      WebkitLineClamp: 3,
                      fontWeight: 500,
                      display: '-webkit-box',
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',

                      [breakpoint('sm')]: {
                          fontSize: calcRem(18),
                          lineHeight: 'normal',
                          minHeight: calcRem(66),
                      },
                      [breakpoint('lg')]: {
                          fontSize: calcRem(21),
                          lineHeight: calcRem(27),
                          minHeight: calcRem(81),
                      },
                      [breakpoint('xl')]: {
                          fontSize: calcRem(22),
                          lineHeight: calcRem(29),
                          minHeight: calcRem(87),
                      },
                  },
              }
            : {},
        cardType === 'large'
            ? {
                  '& [class*="StyledHeadline"],[class*="StyledTeaser"]': {
                      display: '-webkit-box',
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                  },
                  '& [class*="StyledHeadline"]': {
                      fontWeight: 500,
                      fontSize: calcRem(22),
                      lineHeight: calcRem(29),
                      marginBottom: 0,

                      WebkitLineClamp: 3,

                      [breakpoint('xl')]: {
                          fontSize: calcRem(22),
                          lineHeight: calcRem(28),
                      },
                  },
                  '& [class*="StyledTeaser"]': {
                      fontWeight: 400,
                      fontSize: calcRem(17),
                      lineHeight: calcRem(22),
                      marginTop: calcRem(8),

                      WebkitLineClamp: 2,

                      [breakpoint('xl')]: {
                          fontSize: calcRem(17),
                          lineHeight: calcRem(25),
                      },
                  },
              }
            : {},

        cardType === 'large' && collectionType === 'four-card-horizontal'
            ? {
                  '& [class*="StyledHeadline"]': {
                      fontSize: calcRem(22),
                      lineHeight: calcRem(29),
                      [breakpoint('md')]: {
                          fontSize: calcRem(18),
                          lineHeight: calcRem(22),
                      },
                  },
                  '& [class*="StyledTeaser"]': {
                      fontSize: calcRem(16),
                      lineHeight: calcRem(22),
                      [breakpoint('xs')]: {
                          fontSize: calcRem(15),
                          lineHeight: calcRem(20),
                      },
                  },
              }
            : {},

        cardType === 'small-horizontal'
            ? {
                  '& [class*="StyledHeadline"]': {
                      fontWeight: 500,
                      marginBottom: 0,

                      WebkitLineClamp: 3,

                      [breakpoint('xs')]: {
                          fontSize: calcRem(14),
                          lineHeight: 'normal',
                      },

                      [breakpoint('md')]: {
                          fontSize: calcRem(18),
                          lineHeight: calcRem(22),
                      },
                  },
                  '& [class*="StyledTeaser"]': {
                      fontWeight: 400,
                      marginTop: calcRem(8),
                      [breakpoint('xl')]: {
                          fontSize: calcRem(15),
                          lineHeight: calcRem(20),
                      },
                  },
              }
            : {},

        collectionType === 'trending'
            ? {
                  '& [class*="StyledHeadline"]': {
                      fontSize: calcRem(14),
                      lineHeight: calcRem(14),
                      minHeight: 0,
                      [breakpoint('md')]: {
                          fontSize: calcRem(18),
                          lineHeight: calcRem(18),
                      },
                  },
                  '& [class*="StyledTeaser"]': {
                      fontSize: calcRem(15),
                      lineHeight: calcRem(20),
                  },
                  '& [class*="StyledLabelRow"]': {
                      gap: calcRem(10),
                      justifyContent: 'start',
                  },
              }
            : {},
    ],
)

export const StyledCardImageContainer = styled('div')<StyledContentCardProps>(
    ({ cardType }) => [
        cardType === 'medium'
            ? {
                  '& > div': {
                      marginBottom: 0,
                  },
                  marginBottom: calcRem(16),
                  [breakpoint('sm')]: {
                      marginBottom: calcRem(8),
                  },
                  [breakpoint('lg')]: {
                      marginBottom: calcRem(16),
                  },
              }
            : {},
        cardType === 'medium-video-hub'
            ? {
                  '& > div': {
                      marginBottom: 0,
                  },
                  marginBottom: calcRem(16),
                  [breakpoint('sm')]: {
                      marginBottom: calcRem(8),
                  },
                  [breakpoint('lg')]: {
                      marginBottom: calcRem(16),
                  },
              }
            : {},
        cardType === 'large'
            ? {
                  marginBottom: calcRem(16),
                  [breakpoint('sm')]: {
                      marginBottom: 'unset',
                  },
              }
            : {},
    ],
)

export const StyledText = styled(CardText)(({}) => [{}])
interface StyledCardImageProps {
    isVideoPage?: boolean
}
export const StyledCardImage = styled(CardMedia)<StyledCardImageProps>(
    ({ isVideoPage }) => [
        {
            margin: 0,
            '& [class*="image"]': {
                aspectRatio: '3/2',
                '& img': {
                    width: 'auto',
                    height: '100%',
                    translate: isVideoPage ? 'unset' : '-8%',
                },
            },
        },
    ],
)

export const StyledTimeToRead = styled('p')(() => [
    {
        color: colors.sevennews.nero30,
        fontSize: calcRem(12),
        marginBottom: 0,
    },
])

export const StyledLiveStatus = styled('p')(() => [
    {
        display: 'flex',
        alignItems: 'center',
        marginBottom: calcRem(6),
        fontSize: calcRem(12),
    },
])

const blink = keyframes({
    '0%': {
        opacity: 1,
    },
    '50%': { opacity: 0.7 },
    '100%': {
        opacity: 0,
    },
})

export const StyledLiveLabel = styled('p')(({ theme }) => [
    {
        color: theme.sectionValues.primaryColor,
        fontSize: calcRem(12),
        '&::before': {
            content: `''`,
            background: tokens.sevennews.colors.palette.red,
            height: calcRem(5),
            width: calcRem(5),
            marginBottom: calcRem(2),
            marginRight: calcRem(6),
            borderRadius: '50%',
            display: 'inline-block',
            animation: `${blink} 2s ease-out infinite`,
        },
    },
])

export const StyledLinkWrapper = styled(WebLink, {
    shouldForwardProp: createPropFilter<WebLinkProps>()([]),
})<{ gridAreaName?: string; collectionType?: CollectionType }>(
    ({ gridAreaName, collectionType }) => [
        gridAreaName
            ? {
                  gridArea: gridAreaName,
              }
            : {},
        {
            textDecoration: 'none',
            ...(collectionType === 'six-card'
                ? {
                      [breakpoint('md')]: {
                          paddingTop: gridAreaName === 'two' ? calcRem(16) : 0,
                      },
                  }
                : {}),
        },
    ],
)

export const StyledCTAButton = styled('button')(({ theme }) => ({
    fontSize: calcRem(12),
    fontWeight: 600,
    lineHeight: calcRem(15),
    color: tokens.sevennews.colors.palette.red,
    padding: calcRem(8, 22),
    borderColor: theme.sectionValues.primaryColor,
    borderStyle: 'solid',
    borderWidth: calcRem(2),
    borderRadius: calcRem(25),
    background: 'transparent',
    marginTop: calcRem(12),
    cursor: 'pointer',
    '&:hover': {
        color: tokens.sevennews.colors.palette.white,
        background: theme.sectionValues.primaryColor,
    },
}))
export const StyledTimeToReadWrapper = styled('div')(() => [
    {
        display: 'flex',
        alignItems: 'center',
        '& img': {
            marginTop: calcRem(4),
        },
    },
])
export const StyledBreakingLabel = styled('p')(({ theme }) => ({
    color: theme.sectionValues.primaryColor,
    fontSize: calcRem(12),
    marginBottom: calcRem(6),
}))

export const StyledExclusiveLabel = styled('p')(({ theme }) => ({
    color: theme.sectionValues.primaryColor,
    fontSize: calcRem(12),
    marginBottom: calcRem(6),
}))

export const StyledSponsoredLabel = styled('p')(({ theme }) => ({
    color: theme.sectionValues.primaryColor,
    fontSize: calcRem(12),
    marginBottom: calcRem(6),
}))

export const StyledLabelRow = styled('div')(() => [
    {
        flexDirection: 'row',
        display: 'flex',
        justifyContent: 'space-between',
    },
])
